import { Popover as PopoverPrimitive } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/cn';

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverClose = PopoverPrimitive.Close;
export const PopoverAnchor = PopoverPrimitive.Anchor;

type ContentProps = ComponentProps<typeof PopoverPrimitive.Content> & {
  /** Every popover is a dialog; name it ("Filters", "Switch app"). */
  'aria-label': string;
  /** `default` 420px, `wide` 760px — both capped to the viewport minus 32px. */
  width?: 'default' | 'wide' | 'auto';
};

/**
 * A floating panel anchored to its trigger. Portaled to <body> (never
 * clipped by an overflow container); Esc and outside click close it; focus
 * moves in and returns to the trigger.
 */
export function PopoverContent({ className, width = 'default', align = 'start', sideOffset = 6, collisionPadding = 16, ...props }: ContentProps) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          'z-50 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto rounded-pop bg-surface p-2 text-fg shadow-pop outline-none data-[state=open]:animate-pop-in',
          width === 'default' && 'w-[min(420px,calc(100vw-32px))]',
          width === 'wide' && 'w-[min(760px,calc(100vw-32px))]',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

/** Small section label inside a popover (sentence case, muted). */
export function PopoverHint({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('px-1.5 pt-2 pb-1 text-xs font-medium text-muted', className)} {...props} />;
}
